import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getUserPreferencesById,
  updateUserPreferencesById,
} from '../../../../api/General/NotificationPreference';
import type {
  NotificationPreferenceView,
  NotificationPreferenceItemInput,
} from '../../../../api/General/NotificationPreference';
import NotificationPreferenceMatrix from '../../../../components/notifications/NotificationPreferenceMatrix';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '../../../../components/ui/Dialog';
import Button from '../../../../components/ui/Button';
import { Spinner } from '../../../../components/ui/Spinner';
import AppIcon from '../../../../components/AppIcon';

interface UserPreferencesPanelProps {
  open: boolean;
  onClose: () => void;
  userId: string;
  userName: string;
  onSaved: () => void;
}

const UserPreferencesPanel: React.FC<UserPreferencesPanelProps> = ({
  open,
  onClose,
  userId,
  userName,
  onSaved,
}) => {
  const { t } = useTranslation();
  const [preferences, setPreferences] = useState<NotificationPreferenceView[]>(
    []
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const fetchPreferences = useCallback(async () => {
    setLoading(true);
    setError(null);
    setHasChanges(false);
    setSaveSuccess(false);
    try {
      const result = await getUserPreferencesById(userId);
      if (result.errors?.length) {
        setError(
          result.errors[0]?.description || t('Failed to load preferences')
        );
        return;
      }
      setPreferences(result.payload ?? []);
    } catch (err) {
      console.error('Failed to load user preferences:', err);
      setError(t('Failed to load preferences'));
    } finally {
      setLoading(false);
    }
  }, [userId, t]);

  useEffect(() => {
    if (!open || !userId) return;
    fetchPreferences();
  }, [open, userId, fetchPreferences]);

  const handleToggle = useCallback(
    (
      notificationTypeId: number,
      channel: 'email' | 'push' | 'inApp',
      value: boolean
    ) => {
      setPreferences((prev) =>
        prev.map((p) => {
          if (p.notificationTypeId !== notificationTypeId) return p;
          return {
            ...p,
            [channel]: {
              ...p[channel],
              enabled: value,
              source: 'user' as const,
            },
          };
        })
      );
      setHasChanges(true);
      setSaveSuccess(false);
    },
    []
  );

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const items: NotificationPreferenceItemInput[] = preferences.map(
        (p) => ({
          notificationTypeId: p.notificationTypeId,
          emailEnabled: p.email.source === 'user' ? p.email.enabled : null,
          pushEnabled: p.push.source === 'user' ? p.push.enabled : null,
          inAppEnabled: p.inApp.source === 'user' ? p.inApp.enabled : null,
        })
      );

      const result = await updateUserPreferencesById(userId, items);
      if (result.errors?.length) {
        setError(
          result.errors[0]?.description || t('Failed to save preferences')
        );
        return;
      }

      setHasChanges(false);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
      onSaved();
    } catch (err) {
      console.error('Failed to save user preferences:', err);
      setError(t('Failed to save preferences'));
    } finally {
      setSaving(false);
    }
  };

  const handleResetToDefaults = async () => {
    setSaving(true);
    setError(null);
    try {
      // Set all to null to inherit from company defaults
      const resetItems: NotificationPreferenceItemInput[] = preferences.map(
        (p) => ({
          notificationTypeId: p.notificationTypeId,
          emailEnabled: null,
          pushEnabled: null,
          inAppEnabled: null,
        })
      );

      const result = await updateUserPreferencesById(userId, resetItems);
      if (result.errors?.length) {
        setError(
          result.errors[0]?.description || t('Failed to reset preferences')
        );
        return;
      }

      // Refresh to show the new defaults
      await fetchPreferences();
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
      onSaved();
    } catch (err) {
      console.error('Failed to reset user preferences:', err);
      setError(t('Failed to reset preferences'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(val) => !val && onClose()}>
      <DialogContent size="xl">
        <DialogHeader>
          <DialogTitle>
            {t('Notification Preferences')}: {userName}
          </DialogTitle>
          <DialogDescription>
            {t(
              "View and edit this user's notification preferences. Locked channels are controlled by company defaults."
            )}
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          {error && (
            <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive flex items-center gap-2 mb-4">
              <AppIcon name="AlertCircle" size={16} className="shrink-0" />
              {error}
            </div>
          )}

          {saveSuccess && (
            <div className="rounded-lg border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950/30 p-3 text-sm text-green-700 dark:text-green-400 flex items-center gap-2 mb-4">
              <AppIcon name="Check" size={16} className="shrink-0" />
              {t('Preferences updated successfully')}
            </div>
          )}

          {loading ? (
            <div className="flex justify-center py-8">
              <Spinner size="lg" />
            </div>
          ) : (
            <NotificationPreferenceMatrix
              mode="user"
              preferences={preferences}
              onToggle={handleToggle}
            />
          )}
        </DialogBody>

        <DialogFooter>
          <Button
            variant="outline"
            size="sm"
            onClick={handleResetToDefaults}
            disabled={saving || loading}
          >
            <AppIcon
              name="RotateCcw"
              size={14}
              className="ltr:mr-1.5 rtl:ml-1.5"
            />
            {t('Reset to Company Defaults')}
          </Button>
          <div className="flex-1" />
          <Button variant="outline" onClick={onClose} disabled={saving}>
            {t('Cancel')}
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || loading || !hasChanges}
            loading={saving}
          >
            {t('Save Changes')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UserPreferencesPanel;
